import { isLang, type Lang, type StateFrame } from "../shared/protocol.js";
import { $, Conn, WakeLock } from "./common.js";

// UI strings follow the language the attendee picked (or their phone's language before that).
const T = {
  ro: {
    pick: "Alege limba în care vrei să asculți:",
    original: "original",
    originalListenInRoom: "limba vorbitorului — ascultă în sală",
    playing: "Se redă",
    buffering: "Se încarcă sunetul…",
    waitingSpeech: "Conectat — aștept vorbitorul",
    notStarted: "Evenimentul nu a început încă. Lasă pagina deschisă.",
    ended: "Evenimentul s-a încheiat. Mulțumim!",
    reconnecting: "Conexiune pierdută — se reconectează…",
    mute: "Oprește sunetul",
    unmute: "Pornește sunetul",
    tapToResume: "Atinge pentru a relua sunetul",
    originalNote: "Vorbitorul folosește acum această limbă, deci auzi sunetul original din sală.",
    noRelayNote: "Vorbitorul folosește acum această limbă. Ascultă direct în sală sau alege altă limbă.",
    tip: "Folosește căști. Ține ecranul pornit și pagina deschisă.",
  },
  es: {
    pick: "Elige el idioma en el que quieres escuchar:",
    original: "original",
    originalListenInRoom: "idioma del orador — escucha en la sala",
    playing: "Reproduciendo",
    buffering: "Cargando audio…",
    waitingSpeech: "Conectado — esperando al orador",
    notStarted: "El evento aún no ha empezado. Deja esta página abierta.",
    ended: "El evento ha terminado. ¡Gracias!",
    reconnecting: "Conexión perdida — reconectando…",
    mute: "Silenciar",
    unmute: "Activar sonido",
    tapToResume: "Toca para reanudar el audio",
    originalNote: "El orador está hablando en este idioma, así que oyes el sonido original de la sala.",
    noRelayNote: "El orador está hablando en este idioma. Escucha en la sala o elige otro idioma.",
    tip: "Usa auriculares. Mantén la pantalla encendida y la página abierta.",
  },
  en: {
    pick: "Choose the language you want to listen in:",
    original: "original",
    originalListenInRoom: "speaker's language — listen in the room",
    playing: "Playing",
    buffering: "Loading audio…",
    waitingSpeech: "Connected — waiting for the speaker",
    notStarted: "The event hasn't started yet. Keep this page open.",
    ended: "The event has ended. Thank you!",
    reconnecting: "Connection lost — reconnecting…",
    mute: "Mute",
    unmute: "Unmute",
    tapToResume: "Tap to resume audio",
    originalNote: "The speaker is using this language, so you hear the original sound from the room.",
    noRelayNote: "The speaker is using this language. Listen in the room or choose another language.",
    tip: "Use headphones. Keep the screen on and this page open.",
  },
} satisfies Record<Lang, Record<string, string>>;

const code = decodeURIComponent(location.pathname.split("/").filter(Boolean)[1] ?? "").toUpperCase();
const phoneLang = navigator.language.slice(0, 2);
let uiLang: Lang = isLang(phoneLang) ? phoneLang : "en";
const t = () => T[uiLang];

const banner = $("#banner");
const langButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-lang]")];
const playerCard = $("#player");
const muteBtn = $<HTMLButtonElement>("#mute");
const eq = $("#eq");

const wake = new WakeLock();
let channel: Lang | null = null;
let state: StateFrame | null = null;
let conn: Conn | null = null;
let audio: { ctx: AudioContext; node: AudioWorkletNode; gain: GainNode } | null = null;
let muted = false;
let playback = { playing: false, bufferedMs: 0 };
let lastAudioAt = 0;

fetch(`/api/events/${encodeURIComponent(code)}`)
  .then((r) => (r.ok ? r.json() : Promise.reject()))
  .then((ev) => {
    $("#eventName").textContent = ev.name;
    document.title = ev.name;
  })
  .catch(() => {
    $("#eventName").textContent = "Event not found";
    langButtons.forEach((b) => (b.disabled = true));
  });

let audioInit: Promise<void> | null = null;

/** Must run inside the tap handler: iOS only unlocks audio from a user gesture. */
async function ensureAudio() {
  if (audio) {
    if (audio.ctx.state !== "running") await audio.ctx.resume().catch(() => {});
    return;
  }
  // A second tap while the first is still setting up must not create a second context.
  audioInit ??= initAudio().catch((e) => {
    audioInit = null;
    throw e;
  });
  return audioInit;
}

async function initAudio() {
  // Play through the silent switch and alongside other audio on iOS 16.4+.
  const session = (navigator as any).audioSession;
  if (session) session.type = "playback";

  const ctx = new AudioContext({ latencyHint: "playback" });
  const resumed = ctx.resume(); // start inside the gesture, before any await
  await ctx.audioWorklet.addModule("/js/player-worklet.js");
  const node = new AudioWorkletNode(ctx, "player", { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
  const gain = ctx.createGain();
  gain.gain.value = muted ? 0 : 1;
  node.connect(gain).connect(ctx.destination);
  node.port.onmessage = (e) => {
    playback = e.data;
    renderPlayer();
  };
  await resumed;
  audio = { ctx, node, gain };

  // iOS suspends/interrupts the context on calls, Siri, lock screen, etc.
  ctx.onstatechange = () => renderPlayer();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ctx.state !== "running") void ctx.resume().catch(() => {});
  });
}

async function choose(lang: Lang) {
  uiLang = lang;
  document.documentElement.lang = lang;
  const switching = channel !== null && channel !== lang;
  channel = lang;
  await ensureAudio();
  await wake.enable();
  if (switching) audio?.node.port.postMessage({ type: "flush" });

  if (!conn) {
    conn = new Conn({
      hello: () => ({ type: "hello", role: "listener", eventCode: code, channel: channel! }),
      onFrame: (f) => {
        if (f.type === "state") {
          state = f;
          render();
        }
        if (f.type === "error" && f.fatal) showBanner(f.message, "bad");
      },
      onBinary: (buf) => {
        const bytes = new Uint8Array(buf);
        if (bytes[0] !== 0x01 || !audio) return;
        lastAudioAt = Date.now();
        // Copy the payload into its own aligned buffer and hand it to the worklet.
        const pcm = buf.slice(1, 1 + ((buf.byteLength - 1) & ~1));
        audio.node.port.postMessage({ type: "audio", pcm }, [pcm]);
      },
      onStatus: () => render(),
    });
    conn.connect();
  } else if (switching) {
    conn.send({ type: "subscribe", channel: lang });
  }
  render();
}

for (const b of langButtons) b.onclick = () => void choose(b.dataset.lang as Lang);

muteBtn.onclick = () => {
  muted = !muted;
  if (audio) audio.gain.gain.value = muted ? 0 : 1;
  renderPlayer();
};

// Tapping anywhere revives an interrupted iOS audio context.
document.addEventListener("click", () => {
  if (audio && audio.ctx.state !== "running") void audio.ctx.resume().then(renderPlayer, () => {});
});

function render() {
  $("#pick").textContent = t().pick;
  $("#tip").textContent = t().tip;

  for (const b of langButtons) {
    const lang = b.dataset.lang as Lang;
    const isOriginal = state?.sourceLang === lang;
    b.classList.toggle("primary", channel === lang);
    b.querySelector("small")!.textContent = isOriginal
      ? state!.relayOriginal
        ? `(${t().original})`
        : t().originalListenInRoom
      : "";
    b.disabled = !!(isOriginal && state && !state.relayOriginal && channel !== lang);
  }

  const status = conn?.status;
  if (status === "reconnecting" || status === "connecting") {
    showBanner(status === "reconnecting" ? t().reconnecting : "…", "warn");
  } else if (status !== "fatal") {
    banner.classList.add("hidden");
  }
  renderPlayer();
}

function renderPlayer() {
  playerCard.classList.toggle("hidden", !channel);
  if (!channel) return;
  let text: string;
  let note = "";
  if (audio && audio.ctx.state !== "running") text = t().tapToResume;
  else if (state?.status === "idle") text = t().notStarted;
  else if (state?.status === "ended") text = t().ended;
  else if (playback.playing) text = t().playing;
  else if (Date.now() - lastAudioAt < 3000) text = t().buffering;
  else text = t().waitingSpeech;

  if (state?.status === "live" && state.sourceLang === channel) {
    note = state.relayOriginal ? t().originalNote : t().noRelayNote;
  }
  $("#playState").textContent = text;
  $("#note").textContent = note;
  eq.classList.toggle("on", playback.playing && !muted);
  muteBtn.textContent = muted ? t().unmute : t().mute;
}

function showBanner(text: string, kind: "warn" | "bad") {
  banner.textContent = text;
  banner.className = `banner ${kind === "bad" ? "bad" : ""}`;
}

render();
